import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import { makeStyles, Theme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import PencilIcon from "@material-ui/icons/Create";
import Visibility from "@material-ui/icons/Visibility";
import { RefObject, useEffect, useState } from "react";
import { changeAppState } from "../../../../../../../common/appState";
import { spaceBetween } from "../../../../../../../common/util/Array/spaceBetween";
import { AddLinkIcon } from "../../../../../../../icon/AddLinkIcon";
import ShurikenProgress from "../../../../../../shared/Animations/ShurikenProgress";
import { DarkButton } from "../../../../../../shared/Button/DarkButton";
import { WhiteButton } from "../../../../../../shared/Button/WhiteButton";
import { TextField } from "../../../../../../shared/Input/TextField";
import { Markdown } from "../../../../../../shared/Markdown";
import { openOtherUserPanel } from "../../../../../../shared/User/OtherUserPanel/functions";
import { User } from "../../../../../../shared/User/types";
import { signInPanelWidth } from "../../../Panel";
import { InitialView } from "../../MyPageTop/types";
import { useScrollToMyPageItem } from "../../MyPageTop/useScrollToMyPageItem";
import { BioLinks } from "./BioLinks";
import { BioElementToEmphasize } from "./types";

export function BioField({
    user,
    isProfileEditable,
    initialView,
    scrollableContainerRef,
    elementToEmphasize,
}: {
    user: User;
    isProfileEditable: boolean;
    initialView?: InitialView;
    scrollableContainerRef: RefObject<HTMLDivElement>;
    elementToEmphasize?: BioElementToEmphasize;
}) {
    const [editMode, setEditMode] = useState(false);
    const [isAddLinkDialogOpen, setAddLinkDialogOpen] = useState(false);

    const { scrollTargetRef } = useScrollToMyPageItem<HTMLDivElement>({
        scrollableContainerRef,
        initialView,
        itemName: "MyPageBio",
    });

    const field = editMode ? (
        <EditField user={user} setEditMode={setEditMode} />
    ) : (
        <PreviewField
            bio={user.bio}
            setEditMode={setEditMode}
            isProfileEditable={isProfileEditable}
        />
    );

    const [isAddLinkButtonEmphasized, setAddLinkButtonEmphasized] =
        useState(false);
    useEffect(() => {
        if (elementToEmphasize === "AddLinkButton") {
            setAddLinkButtonEmphasized(true);
        }
    }, [elementToEmphasize]);

    const c = useBioFieldStyles({
        editMode,
        isUrlFull: user.bioLinks.length >= 5 || user.sul >= 3,
    });

    return (
        <>
            <div ref={scrollTargetRef} className="fullWidth">
                {field}
            </div>

            <BioLinks
                isAddLinkDialogOpen={isAddLinkDialogOpen}
                closeDialog={() => {
                    setAddLinkDialogOpen(false);
                }}
                user={user}
            />

            <div className={c.buttonsContainer}>
                <DarkButton
                    fullWidth
                    size="small"
                    className={spaceBetween(
                        c.linksButton,
                        isAddLinkButtonEmphasized && c.sizeChangeAnime
                    )}
                    startIcon={<AddLinkIcon />}
                    onClick={() => {
                        setAddLinkDialogOpen(true);
                        setAddLinkButtonEmphasized(false);
                    }}
                >
                    <span className="nowrap">Add Social Link</span>
                </DarkButton>
                <DarkButton
                    fullWidth
                    size="small"
                    className={c.viewAsButton}
                    startIcon={<Visibility />}
                    onClick={() => {
                        openOtherUserPanel(user.userId);
                    }}
                >
                    <span className="nowrap">View As</span>
                </DarkButton>
            </div>
        </>
    );
}
const useBioFieldStyles = makeStyles<
    Theme,
    {
        editMode: boolean;
        isUrlFull: boolean;
    }
>({
    buttonsContainer: {
        marginTop: 5,
        display: "flex",
        justifyContent: "center",
        width: "100%",
    },
    linksButton: ({ editMode, isUrlFull }) => ({
        maxHeight: 30,
        marginRight: isUrlFull ? 0 : 4,
        fontSize: "small",
        maxWidth: editMode || isUrlFull ? 0 : signInPanelWidth,
        minWidth: 0,
        overflow: "hidden",
        padding: "4px 0",
        transition: "all 500ms",
    }),
    sizeChangeAnime: {
        animation: "$sizeChange 5s",
        animationIterationCount: "infinite",
        animationDelay: "1s",
    },
    "@keyframes sizeChange": {
        "0%": {
            transform: "scale(1)",
        },
        "5%": {
            transform: "scale(1.2)",
        },
        "15%": {
            transform: "scale(1)",
        },
        "20%": {
            transform: "scale(1.2)",
        },
        "30%": {
            transform: "scale(1)",
        },
        "35%": {
            transform: "scale(1.2)",
        },
        "45%": {
            transform: "scale(1)",
        },
        "50%": {
            transform: "scale(1.2)",
        },
        "60%": {
            transform: "scale(1)",
        },
        "100%": {
            transform: "scale(1)",
        },
    },
    viewAsButton: ({ editMode }) => ({
        maxHeight: 30,
        fontSize: "small",
        maxWidth: editMode ? 0 : signInPanelWidth,
        minWidth: 0,
        overflow: "hidden",
        padding: "4px 0",
        transition: "all 500ms",
    }),
});

function PreviewField({
    bio,
    setEditMode,
    isProfileEditable,
}: {
    bio: string;
    setEditMode: (editMode: boolean) => void;
    isProfileEditable: boolean;
}) {
    const c = usePreviewFieldStyles({ isProfileEditable });

    return (
        <div
            className={c.previewContainer}
            onClick={() => {
                if (!isProfileEditable) {
                    return;
                }
                setEditMode(true);
            }}
        >
            <Card className={c.markdownContainerCard} elevation={3}>
                <Markdown source={bio} />
            </Card>
            {isProfileEditable && (
                <Button variant="contained" className={c.editButton}>
                    <PencilIcon className={c.pencilIcon} />
                </Button>
            )}
        </div>
    );
}
const usePreviewFieldStyles = makeStyles<Theme, { isProfileEditable: boolean }>(
    ({ palette }) => ({
        pencilIcon: { width: 20, height: 20 },
        previewContainer: {
            width: "100%",
            marginTop: 15,
            position: "relative",
        },
        markdownContainerCard: ({ isProfileEditable }) => ({
            padding: 15,
            cursor: isProfileEditable ? "pointer" : undefined,
        }),
        editButton: {
            backgroundColor: palette.grey[800],
            color: "white",
            maxWidth: 30,
            maxHeight: 30,
            minWidth: 30,
            minHeight: 30,
            position: "absolute",
            bottom: 5,
            right: 5,
            transition: "all 200ms",
            "&:hover": {
                backgroundColor: palette.grey[600],
            },
        },
    })
);

function EditField({
    user,
    setEditMode,
}: {
    user: User;
    setEditMode: (editMode: boolean) => void;
}) {
    const [bio, setBio] = useState(user.bio);

    const error =
        bio.length > 999
            ? "Your bio must be less than 1000 characters."
            : undefined;

    const c = useEditFieldStyles({ isError: !!error });

    const [submitting, setSubmitting] = useState(false);

    return (
        <div className={c.editContainer}>
            <TextField
                label="Bio"
                variant="outlined"
                value={bio}
                multiline
                fullWidth
                onChange={ev => {
                    setBio(ev.target.value || "");
                }}
                inputProps={{
                    style: {
                        paddingBottom: 20,
                    },
                }}
                autoFocus
                helperText={
                    error ? (
                        <div className={c.errorText}>{error}</div>
                    ) : undefined
                }
            />
            <div className={c.buttonContainer}>
                <Button
                    variant="contained"
                    className={c.saveButton}
                    onClick={() => {
                        setSubmitting(true);
                        fetchUpdateBio(user.userId, bio).then(({ user }) => {
                            setSubmitting(false);
                            changeAppState("user", user);
                            setEditMode(false);
                        });
                    }}
                    color="primary"
                    disabled={!!error || submitting}
                >
                    {submitting ? <ShurikenProgress size={20} /> : "Save"}
                </Button>

                <WhiteButton
                    className={c.cancelButton}
                    onClick={() => {
                        setEditMode(false);
                    }}
                >
                    <CloseIcon />
                </WhiteButton>
            </div>
        </div>
    );
}
const useEditFieldStyles = makeStyles<Theme, { isError: boolean }>({
    editContainer: { width: "100%", marginTop: 25, position: "relative" },
    errorText: { color: "red", height: 20 },
    buttonContainer: ({ isError }) => ({
        position: "absolute",
        bottom: isError ? 30 : 5,
        right: 5,
        transition: "all 200ms",
        display: "flex",
        gap: 5,
    }),
    cancelButton: {
        maxWidth: 30,
        maxHeight: 30,
        minWidth: 30,
        minHeight: 30,
    },
    saveButton: {
        maxHeight: 30,
        minHeight: 30,
    },
});

async function fetchUpdateBio(
    userId: number,
    bio: string
): Promise<{ user: User }> {
    const formData = new FormData();
    formData.append("userId", userId.toString());
    formData.append("bio", bio);

    const res = await fetch("api/User/UpdateBio", {
        method: "POST",
        body: formData,
    });
    return res.json();
}
